import React, { FormEvent, useMemo, useRef, useState } from "react";

export type ErpCardMappingSourceType = "EXPLICIT" | "SYSTEM_DEFAULT";
export type ErpCardMappingAuditAction =
  | "ERP_CARD_MAPPING_CREATED"
  | "ERP_CARD_MAPPING_UPDATED"
  | "ERP_CARD_MAPPING_DELETED"
  | "ERP_CARD_MAPPING_ENABLED"
  | "ERP_CARD_MAPPING_DISABLED";

export type ErpCardMapping = {
  id: string;
  sourceCardName: string;
  targetErpCardName: string;
  enabled: boolean;
  sourceType: ErpCardMappingSourceType;
  version: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  productionAllowed?: boolean;
  latest?: boolean;
  fieldConfirmation?: "FIELD_CONFIRMED";
};

export type ErpCardMappingAudit = {
  id: string;
  action: ErpCardMappingAuditAction;
  actor: string;
  timestamp: string;
  before: ErpCardMapping | null;
  after: ErpCardMapping | null;
};

export type ErpCardSourceSummary = {
  sourceCardName: string;
  eventCount: number;
  signedAmount: number;
};

export type ErpCardImportGroupStatus =
  | "FILE_CREATED"
  | "IMPORT_STARTED"
  | "VERIFY_REQUIRED"
  | "VERIFIED"
  | "FAILED";

export type ErpCardImportGroupSummary = {
  groupId: string;
  targetErpCardName: string;
  eventCount: number;
  signedAmount: number;
  status: ErpCardImportGroupStatus;
  sourceCardBreakdown: ErpCardSourceSummary[];
  mappingIds: string[];
  fileName?: string;
  fileHash?: string;
};

export type ErpCardRoutingBatchSummary = {
  parentBatchId: string;
  eventCount: number;
  netAmount: number;
  status: "VERIFIED" | "PARTIAL_MAPPING_REQUIRED" | "PARTIAL_VERIFY_REQUIRED" | "PARTIAL_FAILED";
  groups: ErpCardImportGroupSummary[];
};

export type ErpCardRoutingSnapshot = {
  capturedAt: string;
  actor: string;
  mappings: ErpCardMapping[];
  erpCardMaster: {
    capturedAt: string;
    cards: string[];
    source: "SYNTHETIC_UIA_SNAPSHOT";
  };
  pendingSources: ErpCardSourceSummary[];
  activeImportGroupMappingIds: string[];
  batch: ErpCardRoutingBatchSummary;
  pilotGates: Array<{
    id: string;
    label: string;
    ready: boolean;
  }>;
};

export type ErpCardMasterSnapshot = {
  options: string[];
  capturedAt: string;
  agentId?: string;
  hostName?: string;
  uiIdentityHash?: string;
  masterHash?: string;
};

export type ErpCardActiveGroup = {
  groupId: string;
  parentBatchId: string;
  entityId?: "INCHEON" | "AGRICULTURAL";
  erpEntityName?: string;
  targetErpCardName: string;
  status: string;
  mappingSnapshot: Array<{
    mappingId: string;
    mappingVersion: number;
  }>;
  eventCount?: number;
  positiveAmount?: number;
  negativeAmount?: number;
  netAmount?: number;
  sourceCardBreakdown?: ErpCardSourceSummary[];
  createdAt?: string;
  updatedAt?: string;
  fileName?: string;
  fileHash?: string;
  importResultStatus?: string;
  importExpectedCount?: number | null;
  importRegisteredCount?: number | null;
  importCountMatch?: boolean;
  importResultMessage?: string;
  importResultParserVersion?: string;
  automaticRetry?: boolean;
  errorCode?: string;
  lookupVerificationStatus?: string;
  lookupObservedAt?: string;
  lookupMatchChecks?: Array<{
    eventId: string;
    dateMatch: boolean;
    vendorMatch: boolean;
    amountMatch: boolean;
    noteMatch: boolean;
  }>;
  fourWayReconciliation?: {
    source: ErpFourWayStage | null;
    xls: ErpFourWayStage | null;
    importResult: (ErpFourWayStage & { evidenceScope?: "COUNT_ONLY" }) | null;
    lookup: ErpFourWayStage | null;
    matched: boolean;
  } | null;
  verificationProbes?: Array<{
    eventId: string;
    occurredOn: string;
    vendorCode: string;
    signedAmount: number;
    note: string;
  }>;
  agentId?: string;
  agentHost?: string;
};

type ErpFourWayStage = {
  count: number;
  positiveAmount: number | null;
  negativeAmount: number | null;
  netAmount: number | null;
};

export type ErpCardParentBatchHistory = {
  parentBatchId: string;
  status: string;
  eventCount: number;
  netAmount: number;
  verifiedCount: number;
  createdAt: string;
};

export type ErpCardPendingEvent = {
  eventId: string;
  cardIssuerName?: string;
  signedErpAmount: number;
};

export type ErpFieldDoubleCheckStatus = "PENDING" | "MATCHED" | "MISMATCH";

export type ErpCardOperationalContract = {
  persistenceExpectation?: "EXPECTED";
  fieldVerificationStatus?: "PENDING" | "VERIFIED" | "VERIFY_REQUIRED";
  livePilotApproved?: boolean;
  vendorFieldDesignation?: string;
  vendorRecognitionMethod?: string;
  vendorFieldMapping?: string;
  vendorCodeLeadingZeroReadbackStatus?: ErpFieldDoubleCheckStatus;
  groupExactCardReadbackStatus?: ErpFieldDoubleCheckStatus;
  groupXlsReadbackStatus?: ErpFieldDoubleCheckStatus;
};

export type ErpOwnerEntityResolutionPreview = {
  entityId?: string;
  canonicalEntityId?: string;
  sourceEntityName?: string;
  erpEntityName: string;
  resolutionSource: string;
  resolved: boolean;
  eventCount?: number;
  reasonCode?: string;
};

export type ErpOwnerCardResolutionPreview = {
  sourceCardName: string;
  targetErpCardName: string;
  resolutionType: "OWNER_ALIAS" | "EXACT_NAME" | "EXPLICIT_OVERRIDE" | "UNRESOLVED";
  resolved: boolean;
  eventCount?: number;
  reasonCode?: string;
};

export type ErpOwnerGroupSourceBreakdown = {
  sourceCardName: string;
  eventCount: number;
  approvalCount: number;
  cancellationCount: number;
  positiveAmount: number;
  negativeAmount: number;
  netAmount: number;
};

export type ErpOwnerGroupPreview = {
  groupId: string;
  entityId?: "INCHEON" | "AGRICULTURAL";
  erpEntityName?: string;
  targetErpCardName: string;
  resolutionTypes: Array<"OWNER_ALIAS" | "EXACT_NAME" | "EXPLICIT_OVERRIDE">;
  sourceBreakdown: ErpOwnerGroupSourceBreakdown[];
  eventCount: number;
  approvalCount: number;
  cancellationCount: number;
  positiveAmount: number;
  negativeAmount: number;
  netAmount: number;
  routeSnapshots?: Array<Omit<ErpOwnerCardResolutionPreview, "resolved"> & { resolved?: boolean }>;
  events?: Array<{
    entityId?: string;
    canonicalEntityId?: string;
    erpEntityName?: string;
    entitySource?: string;
    sourceCardName?: string;
    targetErpCardName?: string;
    cardResolutionType?: "OWNER_ALIAS" | "EXACT_NAME" | "EXPLICIT_OVERRIDE" | "UNRESOLVED";
  }>;
};

export type ErpOwnerRoutingPreview = {
  schemaVersion?: string;
  mode?: "EXPORT_PREVIEW";
  snapshotId?: string;
  snapshotHash?: string;
  sourceReadAt?: string;
  ownerRuleVersion?: string;
  headers?: string[];
  sourceSummary?: ErpFourWayStage;
  routedSummary?: ErpFourWayStage;
  selectedTarget?: {
    targetErpCardName?: string;
    groupId?: string;
    resolutionType?: "OWNER_ALIAS" | "EXACT_NAME" | "EXPLICIT_OVERRIDE" | "MIXED";
    masterHash?: string;
  } | null;
  entities?: ErpOwnerEntityResolutionPreview[];
  cards?: ErpOwnerCardResolutionPreview[];
  groups?: ErpOwnerGroupPreview[];
  reasonMatrix?: Array<{
    entityResolution?: string;
    entityId?: string;
    entitySource?: string;
    noteLabel?: string;
    sourceCardName?: string;
    targetErpCardName?: string;
    cardResolutionType?: "OWNER_ALIAS" | "EXACT_NAME" | "EXPLICIT_OVERRIDE" | "UNRESOLVED";
    exclusionReasons?: string[];
  }>;
  unresolved?: Array<{
    kind: "ENTITY" | "CARD";
    source: string;
    reasonCode: string;
  }>;
  importAllowed?: boolean;
  pilotLocked?: boolean;
  reservationPerformed?: boolean;
  productionBatchReserved?: boolean;
  productionWritePerformed?: boolean;
};

export type ErpPrepareOnlyEvidence = {
  schemaVersion?: string;
  mode?: "PREPARE_ONLY";
  status?: string;
  code?: string;
  capturedAt?: string;
  profileVersion?: string;
  batchId?: string;
  groupId?: string;
  progress?: string;
  allowImport?: boolean;
  xls?: {
    fileName?: string;
    sha256?: string;
    sheetName?: string;
    headerCount?: number;
    dataRowCount?: number;
    headerOnly?: boolean;
    biff8Verified?: boolean;
  };
  businessSideEffects?: {
    importInvoke?: number;
    erpWrite?: number;
    network?: number;
  };
};

export type ErpCardRoutingRuntime = {
  mappings: ErpCardMapping[];
  erpCardMaster: ErpCardMasterSnapshot | null;
  activeGroups: ErpCardActiveGroup[];
  groupHistory?: ErpCardActiveGroup[];
  parentBatches?: ErpCardParentBatchHistory[];
  operationalContract?: ErpCardOperationalContract;
  ownerRulePreview?: ErpOwnerRoutingPreview | null;
  prepareOnlyEvidence?: ErpPrepareOnlyEvidence | null;
  pendingEvents: ErpCardPendingEvent[];
  loading?: boolean;
  saving?: boolean;
  error?: string;
  onReload?: () => Promise<unknown>;
  onCreateMapping?: (mapping: {
    sourceCardName: string;
    targetErpCardName: string;
    enabled: boolean;
  }) => Promise<unknown>;
  onUpdateMapping?: (
    mappingId: string,
    patch: { sourceCardName: string; targetErpCardName: string }
  ) => Promise<unknown>;
  onDeleteMapping?: (mappingId: string) => Promise<unknown>;
  onToggleMapping?: (mappingId: string, enabled: boolean) => Promise<unknown>;
  onNotify?: (message: string) => void;
};

type EffectiveRoute = ErpCardSourceSummary & {
  targetErpCardName: string | null;
  resolution: "EXPLICIT" | "CARD_MAPPING_REQUIRED";
  mappingId: string | null;
};

type Props = {
  snapshot?: ErpCardRoutingSnapshot;
  runtime?: ErpCardRoutingRuntime;
};

const MAPPING_PROTECTION_STATUSES = new Set([
  "IMPORT_STARTED",
  "IMPORT_SUCCEEDED",
  "ERP_VERIFICATION_STARTED",
  "VERIFY_REQUIRED",
]);

export const isMappingProtectedGroupStatus = (status: string) =>
  MAPPING_PROTECTION_STATUSES.has(status.trim().toUpperCase());

const STATUS_STYLES: Record<ErpCardImportGroupStatus, string> = {
  FILE_CREATED: "bg-blue-100 text-blue-900",
  IMPORT_STARTED: "bg-amber-100 text-amber-950",
  VERIFY_REQUIRED: "bg-orange-100 text-orange-950",
  VERIFIED: "bg-emerald-100 text-emerald-900",
  FAILED: "bg-red-100 text-red-950",
};

const STATUS_LABELS: Record<ErpCardImportGroupStatus, string> = {
  FILE_CREATED: "파일 생성",
  IMPORT_STARTED: "ERP 가져오기 중",
  VERIFY_REQUIRED: "확인 필요",
  VERIFIED: "검증 완료",
  FAILED: "실패",
};

const formatFourWayStage = (stage: ErpFourWayStage | null | undefined) => {
  if (!stage) return "증거 없음";
  if (stage.positiveAmount === null || stage.negativeAmount === null || stage.netAmount === null) {
    return `${stage.count}건 · 등록건수 증거만 확인`;
  }
  return `${stage.count}건 · +${formatMoney(stage.positiveAmount)} / ${formatMoney(stage.negativeAmount)} / Net ${formatMoney(stage.netAmount)}`;
};

export const ERP_CARD_ROUTING_QA_SNAPSHOT: ErpCardRoutingSnapshot = {
  capturedAt: "2026-08-13 15:20 KST",
  actor: "IT_ADMIN_QA",
  mappings: [
    {
      id: "MAP-WOORI-001",
      sourceCardName: "우리카드",
      targetErpCardName: "BC카드",
      enabled: true,
      sourceType: "EXPLICIT",
      version: 7,
      createdAt: "2026-08-12 10:00 KST",
      updatedAt: "2026-08-13 09:10 KST",
      createdBy: "IT_ADMIN_QA",
      updatedBy: "IT_ADMIN_QA",
      productionAllowed: true,
      latest: true,
      fieldConfirmation: "FIELD_CONFIRMED",
    },
    {
      id: "MAP-IBK-001",
      sourceCardName: "기업카드",
      targetErpCardName: "BC카드",
      enabled: true,
      sourceType: "EXPLICIT",
      version: 3,
      createdAt: "2026-08-13 09:00 KST",
      updatedAt: "2026-08-13 09:00 KST",
      createdBy: "FIELD_CONFIRMED_QA",
      updatedBy: "FIELD_CONFIRMED_QA",
      productionAllowed: true,
      latest: true,
      fieldConfirmation: "FIELD_CONFIRMED",
    },
    {
      id: "MAP-SAMSUNG-001",
      sourceCardName: "삼성카드",
      targetErpCardName: "삼성카드",
      enabled: true,
      sourceType: "EXPLICIT",
      version: 2,
      createdAt: "2026-08-12 10:05 KST",
      updatedAt: "2026-08-12 10:05 KST",
      createdBy: "IT_ADMIN_QA",
      updatedBy: "IT_ADMIN_QA",
      productionAllowed: true,
      latest: true,
      fieldConfirmation: "FIELD_CONFIRMED",
    },
  ],
  erpCardMaster: {
    capturedAt: "2026-08-13 11:20 KST",
    cards: ["BC카드", "삼성카드", "신한카드", "롯데카드", "현대카드"],
    source: "SYNTHETIC_UIA_SNAPSHOT",
  },
  pendingSources: [
    { sourceCardName: "우리카드", eventCount: 5, signedAmount: 980000 },
    { sourceCardName: "기업카드", eventCount: 2, signedAmount: 440000 },
    { sourceCardName: "삼성카드", eventCount: 4, signedAmount: 750000 },
    { sourceCardName: "새로운카드", eventCount: 3, signedAmount: 330000 },
  ],
  activeImportGroupMappingIds: ["MAP-WOORI-001"],
  batch: {
    parentBatchId: "B260813-001",
    eventCount: 18,
    netAmount: 3850000,
    status: "PARTIAL_VERIFY_REQUIRED",
    groups: [
      {
        groupId: "G001",
        targetErpCardName: "삼성카드",
        eventCount: 4,
        signedAmount: 750000,
        status: "VERIFIED",
        sourceCardBreakdown: [
          { sourceCardName: "삼성카드", eventCount: 4, signedAmount: 750000 },
        ],
        mappingIds: ["MAP-SAMSUNG-001"],
        fileName: "cnfood_erp_B260813-001_G001.xls",
      },
      {
        groupId: "G002",
        targetErpCardName: "BC카드",
        eventCount: 7,
        signedAmount: 1420000,
        status: "IMPORT_STARTED",
        sourceCardBreakdown: [
          { sourceCardName: "우리카드", eventCount: 5, signedAmount: 980000 },
          { sourceCardName: "기업카드", eventCount: 2, signedAmount: 440000 },
        ],
        mappingIds: ["MAP-WOORI-001", "MAP-IBK-001"],
        fileName: "cnfood_erp_B260813-001_G002.xls",
      },
      {
        groupId: "G003",
        targetErpCardName: "신한카드",
        eventCount: 7,
        signedAmount: 1680000,
        status: "VERIFY_REQUIRED",
        sourceCardBreakdown: [
          { sourceCardName: "신한카드", eventCount: 7, signedAmount: 1680000 },
        ],
        mappingIds: [],
        fileName: "cnfood_erp_B260813-001_G003.xls",
      },
    ],
  },
  pilotGates: [
    { id: "mapping-active", label: "Mapping ACTIVE", ready: true },
    { id: "production-allowed", label: "Mapping productionAllowed", ready: true },
    { id: "mapping-latest", label: "Mapping latest", ready: true },
    { id: "persistence", label: "Persistence EXPECTED", ready: true },
    { id: "vendor-field-contract", label: "Vendor Field Contract 3값", ready: false },
    { id: "readback-double-check", label: "Readback Double Check", ready: false },
    { id: "verification", label: "ERP Field Verification", ready: false },
    { id: "approval", label: "LIVE_PILOT_APPROVED", ready: false },
  ],
};

const normalizeCardName = (value: string) => value.trim().replace(/\s+/g, " ");

const getOwnerEntitySourceLabel = (entity: ErpOwnerEntityResolutionPreview) => {
  const explicit = entity.sourceEntityName?.trim();
  if (explicit) return explicit;
  if (entity.entityId === "INCHEON" || entity.canonicalEntityId === "branch_incheon") {
    return "인천법인";
  }
  if (entity.entityId === "AGRICULTURAL" || entity.canonicalEntityId === "branch_hwaseong") {
    return "농업법인";
  }
  return entity.entityId || entity.canonicalEntityId || "법인 미확인";
};

const deriveOwnerEntityPreview = (preview: ErpOwnerRoutingPreview) => {
  if (preview.entities?.length) return preview.entities;
  const buckets = new Map<string, ErpOwnerEntityResolutionPreview>();
  (preview.groups || []).flatMap((group) => group.events || []).forEach((event) => {
    const key = `${event.entityId || event.canonicalEntityId || ""}:${event.erpEntityName || ""}:${event.entitySource || ""}`;
    const current = buckets.get(key) || {
      entityId: event.entityId,
      canonicalEntityId: event.canonicalEntityId,
      erpEntityName: event.erpEntityName || "",
      resolutionSource: event.entitySource || "",
      resolved: Boolean(event.erpEntityName && event.entitySource && event.entitySource !== "UNRESOLVED"),
      eventCount: 0,
    };
    current.eventCount = Number(current.eventCount || 0) + 1;
    buckets.set(key, current);
  });
  return Array.from(buckets.values());
};

const deriveOwnerCardPreview = (preview: ErpOwnerRoutingPreview) => {
  if (preview.cards?.length) return preview.cards;
  const groups = preview.groups || [];
  const routes = groups.flatMap((group) => group.routeSnapshots || []);
  if (routes.length) {
    return routes.map((route) => ({
      ...route,
      resolved: route.resolved !== false && route.resolutionType !== "UNRESOLVED",
      eventCount: route.eventCount ?? groups
        .flatMap((group) => group.sourceBreakdown)
        .find((source) => source.sourceCardName === route.sourceCardName)?.eventCount,
    }));
  }
  return groups.flatMap((group) => group.events || []).reduce<ErpOwnerCardResolutionPreview[]>((rows, event) => {
    if (!event.sourceCardName || !event.cardResolutionType) return rows;
    const existing = rows.find((row) =>
      row.sourceCardName === event.sourceCardName &&
      row.targetErpCardName === (event.targetErpCardName || "") &&
      row.resolutionType === event.cardResolutionType
    );
    if (existing) {
      existing.eventCount = Number(existing.eventCount || 0) + 1;
      return rows;
    }
    rows.push({
      sourceCardName: event.sourceCardName,
      targetErpCardName: event.targetErpCardName || "",
      resolutionType: event.cardResolutionType,
      resolved: event.cardResolutionType !== "UNRESOLVED",
      eventCount: 1,
    });
    return rows;
  }, []);
};

export const summarizePendingCardEvents = (
  events: ErpCardPendingEvent[]
): ErpCardSourceSummary[] => {
  const summaries = new Map<string, ErpCardSourceSummary>();
  events.forEach((event) => {
    const sourceCardName = normalizeCardName(event.cardIssuerName || "미확인") || "미확인";
    const current = summaries.get(sourceCardName) || {
      sourceCardName,
      eventCount: 0,
      signedAmount: 0,
    };
    current.eventCount += 1;
    current.signedAmount += Number(event.signedErpAmount) || 0;
    summaries.set(sourceCardName, current);
  });
  return Array.from(summaries.values()).sort((left, right) =>
    left.sourceCardName.localeCompare(right.sourceCardName, "ko")
  );
};

export const resolveEffectiveCardRoutes = (
  sources: ErpCardSourceSummary[],
  mappings: ErpCardMapping[],
  erpMasterCards: string[]
): EffectiveRoute[] => {
  const master = new Set(erpMasterCards.map(normalizeCardName));
  const explicit = new Map(
    mappings
      .filter(
        (mapping) =>
          mapping.enabled &&
          mapping.sourceType === "EXPLICIT" &&
          mapping.productionAllowed === true &&
          mapping.latest === true &&
          mapping.fieldConfirmation === "FIELD_CONFIRMED"
      )
      .map((mapping) => [normalizeCardName(mapping.sourceCardName), mapping] as const)
  );

  return sources.map((source) => {
    const sourceCardName = normalizeCardName(source.sourceCardName);
    const explicitMapping = explicit.get(sourceCardName);
    if (explicitMapping) {
      const target = normalizeCardName(explicitMapping.targetErpCardName);
      return {
        ...source,
        sourceCardName,
        targetErpCardName: master.has(target) ? target : null,
        resolution: master.has(target) ? "EXPLICIT" : "CARD_MAPPING_REQUIRED",
        mappingId: explicitMapping.id,
      };
    }

    return {
      ...source,
      sourceCardName,
      targetErpCardName: null,
      resolution: "CARD_MAPPING_REQUIRED",
      mappingId: null,
    };
  });
};

export const groupEffectiveCardRoutes = (routes: EffectiveRoute[]) => {
  const groups = new Map<
    string,
    { targetErpCardName: string; eventCount: number; signedAmount: number; sources: ErpCardSourceSummary[] }
  >();

  routes.forEach((route) => {
    if (!route.targetErpCardName) return;
    const current = groups.get(route.targetErpCardName) || {
      targetErpCardName: route.targetErpCardName,
      eventCount: 0,
      signedAmount: 0,
      sources: [],
    };
    current.eventCount += route.eventCount;
    current.signedAmount += route.signedAmount;
    current.sources.push({
      sourceCardName: route.sourceCardName,
      eventCount: route.eventCount,
      signedAmount: route.signedAmount,
    });
    groups.set(route.targetErpCardName, current);
  });

  return Array.from(groups.values()).sort((a, b) =>
    a.targetErpCardName.localeCompare(b.targetErpCardName, "ko")
  );
};

const formatMoney = (value: number) => `${value.toLocaleString("ko-KR")}원`;
const shortEvidenceHash = (value: string | undefined) =>
  value ? `${value.slice(0, 12)}…${value.slice(-8)}` : "미연결";
const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;

const ErpCardRoutingPanel = ({ snapshot = ERP_CARD_ROUTING_QA_SNAPSHOT, runtime }: Props) => {
  const [mappings, setMappings] = useState<ErpCardMapping[]>(() => snapshot.mappings);
  const [audits, setAudits] = useState<ErpCardMappingAudit[]>([]);
  const [sourceCardName, setSourceCardName] = useState("");
  const [targetErpCardName, setTargetErpCardName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState(
    runtime
      ? "서버 ERP 카드사 설정을 표시합니다. 변경은 IT_ADMIN API에서 다시 검증됩니다."
      : "Synthetic 화면입니다. API·저장소·ERP에는 연결되지 않습니다."
  );
  const [masterCapturedAt, setMasterCapturedAt] = useState(snapshot.erpCardMaster.capturedAt);
  const [autoImportRequested, setAutoImportRequested] = useState(false);
  const [runtimeBusyAction, setRuntimeBusyAction] = useState("");
  const sequence = useRef(1);

  const runtimeMappings = runtime?.mappings || [];
  const effectiveMappings = runtime ? runtimeMappings : mappings;
  const runtimeMasterCards = runtime?.erpCardMaster?.options || [];
  const effectiveMasterCards = runtime ? runtimeMasterCards : snapshot.erpCardMaster.cards;
  const runtimePendingSources = useMemo(
    () => summarizePendingCardEvents(runtime?.pendingEvents || []),
    [runtime?.pendingEvents]
  );
  const effectivePendingSources = runtime ? runtimePendingSources : snapshot.pendingSources;
  const cardMasterEvidenceReady = Boolean(
    runtime?.erpCardMaster?.capturedAt &&
    runtime?.erpCardMaster?.options.length &&
    runtime?.erpCardMaster?.uiIdentityHash &&
    runtime?.erpCardMaster?.masterHash
  );
  const runtimeGroupHistory = runtime?.groupHistory?.length
    ? runtime.groupHistory
    : runtime?.activeGroups || [];
  const runtimeActiveMappingIds = useMemo(
    () =>
      new Set(
        (runtime?.activeGroups || [])
          .filter((group) => isMappingProtectedGroupStatus(group.status))
          .flatMap((group) => group.mappingSnapshot.map((mapping) => mapping.mappingId))
      ),
    [runtime?.activeGroups]
  );
  const activeMappingIds = useMemo(
    () => runtime ? runtimeActiveMappingIds : new Set(snapshot.activeImportGroupMappingIds),
    [runtime, runtimeActiveMappingIds, snapshot.activeImportGroupMappingIds]
  );
  const effectiveRoutes = useMemo(
    () => resolveEffectiveCardRoutes(effectivePendingSources, effectiveMappings, effectiveMasterCards),
    [effectiveMappings, effectiveMasterCards, effectivePendingSources]
  );
  const effectiveGroups = useMemo(() => groupEffectiveCardRoutes(effectiveRoutes), [effectiveRoutes]);
  const mappingRequired = effectiveRoutes.filter((route) => route.targetErpCardName === null);
  const operationalContract = runtime?.operationalContract;
  const ownerRulePreview = runtime?.ownerRulePreview || null;
  const prepareOnlyEvidence = runtime?.prepareOnlyEvidence || null;
  const ownerEntityPreview = ownerRulePreview ? deriveOwnerEntityPreview(ownerRulePreview) : [];
  const ownerCardPreview = ownerRulePreview ? deriveOwnerCardPreview(ownerRulePreview) : [];
  const ownerGroupPreview = ownerRulePreview?.groups || [];
  const ownerReasonMatrixUnresolved = ownerRulePreview?.reasonMatrix?.flatMap((row) => {
    const unresolved: Array<{ kind: "ENTITY" | "CARD"; source: string; reasonCode: string }> = [];
    if (row.entityResolution && row.entityResolution !== "RESOLVED") {
      unresolved.push({
        kind: "ENTITY",
        source: row.entityId || row.entitySource || "법인 미확인",
        reasonCode: row.entityResolution,
      });
    }
    if (row.cardResolutionType === "UNRESOLVED") {
      unresolved.push({
        kind: "CARD",
        source: row.sourceCardName || "카드사 미확인",
        reasonCode: row.exclusionReasons?.find((reason) =>
          ["CARD_MAPPING_REQUIRED", "ERP_CARD_NOT_FOUND", "ERP_CARD_SOURCE_REQUIRED", "ERP_CARD_MAPPING_POLICY_CONFLICT"].includes(reason)
        ) || "CARD_MAPPING_REQUIRED",
      });
    }
    return unresolved;
  }) || [];
  const ownerRuleUnresolved = ownerRulePreview
    ? Array.from(new Map([
        ...(ownerRulePreview.unresolved || []),
        ...ownerEntityPreview
          .filter((entity) => entity.resolved === false)
          .map((entity) => ({
            kind: "ENTITY" as const,
            source: getOwnerEntitySourceLabel(entity),
            reasonCode: entity.reasonCode || "ERP_ENTITY_UNRESOLVED",
          })),
        ...ownerCardPreview
          .filter((card) => card.resolved === false || card.resolutionType === "UNRESOLVED")
          .map((card) => ({
            kind: "CARD" as const,
            source: card.sourceCardName,
            reasonCode: card.reasonCode || "CARD_MAPPING_REQUIRED",
          })),
        ...ownerReasonMatrixUnresolved,
      ].map((item) => [`${item.kind}:${item.source}:${item.reasonCode}`, item])).values())
    : [];
  const ownerRuleHasUnresolved = ownerRuleUnresolved.length > 0;
  const ownerRuleEvidenceReady = Boolean(
    ownerEntityPreview.length &&
    ownerCardPreview.length &&
    ownerGroupPreview.length
  );
  const ownerRuleLockIntact = Boolean(
    ownerRulePreview?.importAllowed === false &&
    ownerRulePreview?.pilotLocked === true &&
    ownerRulePreview?.reservationPerformed === false &&
    ownerRulePreview?.productionBatchReserved === false &&
    ownerRulePreview?.productionWritePerformed === false
  );
  const previewMetadataReady = Boolean(
    ownerRulePreview?.schemaVersion &&
    ownerRulePreview?.mode === "EXPORT_PREVIEW" &&
    ownerRulePreview?.snapshotId &&
    ownerRulePreview?.sourceReadAt &&
    ownerRulePreview?.snapshotHash
  );
  const headerOnlySchemaReady = Boolean(
    prepareOnlyEvidence?.mode === "PREPARE_ONLY" &&
    prepareOnlyEvidence?.xls?.sheetName === "ERP_CARD_DRAFT" &&
    prepareOnlyEvidence?.xls?.headerCount === 10 &&
    prepareOnlyEvidence?.xls?.dataRowCount === 0 &&
    prepareOnlyEvidence?.xls?.headerOnly === true &&
    prepareOnlyEvidence?.xls?.biff8Verified === true
  );
  const prepareSideEffectLockIntact = Boolean(
    prepareOnlyEvidence?.mode === "PREPARE_ONLY" &&
    prepareOnlyEvidence?.allowImport === false &&
    prepareOnlyEvidence?.businessSideEffects?.importInvoke === 0 &&
    prepareOnlyEvidence?.businessSideEffects?.erpWrite === 0 &&
    prepareOnlyEvidence?.businessSideEffects?.network === 0
  );
  const persistenceExpected = operationalContract?.persistenceExpectation === "EXPECTED";
  const fieldVerificationStatus = operationalContract?.fieldVerificationStatus || "PENDING";
  const vendorFieldContract = [
    operationalContract?.vendorFieldDesignation,
    operationalContract?.vendorRecognitionMethod,
    operationalContract?.vendorFieldMapping,
  ];
  const vendorFieldContractReady =
    Boolean(runtime) && vendorFieldContract.every((value) => Boolean(value?.trim()));
  const doubleChecks = [
    {
      id: "vendor-code-leading-zero",
      label: "Canonical vendorCode leading zero",
      status: operationalContract?.vendorCodeLeadingZeroReadbackStatus || "PENDING",
    },
    {
      id: "group-exact-card",
      label: "Group exact card readback",
      status: operationalContract?.groupExactCardReadbackStatus || "PENDING",
    },
    {
      id: "group-xls-readback",
      label: "Group XLS readback",
      status: operationalContract?.groupXlsReadbackStatus || "PENDING",
    },
  ] as const;
  const doubleCheckReady = doubleChecks.every((item) => item.status === "MATCHED");
  const doubleCheckMismatch = doubleChecks.some((item) => item.status === "MISMATCH");
  const productionAllowedMappings = effectiveMappings.filter(
    (mapping) => mapping.enabled && mapping.productionAllowed === true
  );
  const productionMappings = effectiveMappings.filter(
    (mapping) =>
      mapping.enabled &&
      mapping.productionAllowed === true &&
      mapping.latest === true &&
      mapping.fieldConfirmation === "FIELD_CONFIRMED"
  );
  const effectivePilotGates = runtime
    ? [
        {
          id: "mapping-active",
          label: "Mapping ACTIVE",
          ready: effectiveMappings.some((mapping) => mapping.enabled),
        },
        {
          id: "production-allowed",
          label: "Mapping productionAllowed",
          ready: productionAllowedMappings.length > 0,
        },
        {
          id: "mapping-latest",
          label: "Mapping latest",
          ready: productionMappings.length > 0,
        },
        { id: "persistence", label: "Persistence EXPECTED", ready: persistenceExpected },
        {
          id: "vendor-field-contract",
          label: "Vendor Field Contract 3값",
          ready: vendorFieldContractReady,
        },
        {
          id: "readback-double-check",
          label: "Readback Double Check",
          ready: doubleCheckReady && !doubleCheckMismatch,
        },
        {
          id: "verification",
          label: "ERP Field Verification",
          ready: fieldVerificationStatus === "VERIFIED",
        },
        {
          id: "approval",
          label: "LIVE_PILOT_APPROVED",
          ready: operationalContract?.livePilotApproved === true,
        },
      ]
    : snapshot.pilotGates;
  const readyGateCount = effectivePilotGates.filter((gate) => gate.ready).length;
  const allPilotGatesReady = !runtime && readyGateCount === effectivePilotGates.length;
  const effectiveAutoImportEnabled =
    autoImportRequested && allPilotGatesReady && !doubleCheckMismatch;

  const resetForm = () => {
    setSourceCardName("");
    setTargetErpCardName("");
    setEditingId(null);
  };

  const recordAudit = (
    action: ErpCardMappingAuditAction,
    before: ErpCardMapping | null,
    after: ErpCardMapping | null
  ) => {
    const currentSequence = sequence.current;
    sequence.current += 1;
    setAudits((current) => [
      {
        id: `AUDIT-${currentSequence}`,
        action,
        actor: snapshot.actor,
        timestamp: `${snapshot.capturedAt} · SIM-${currentSequence}`,
        before,
        after,
      },
      ...current,
    ]);
  };

  const isDuplicateSource = (source: string, exceptId: string | null) => {
    const normalizedSource = normalizeCardName(source);
    return effectiveMappings.some(
      (mapping) =>
        mapping.id !== exceptId && normalizeCardName(mapping.sourceCardName) === normalizedSource
    );
  };

  const submitMapping = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const source = normalizeCardName(sourceCardName);
    const target = normalizeCardName(targetErpCardName);
    if (!source || !target) {
      setStatusMessage("KCP 카드사와 천년경영 카드사를 모두 입력해 주세요.");
      return;
    }
    if (isDuplicateSource(source, editingId)) {
      setStatusMessage(`중복 Source Mapping 차단: ${source} 매핑이 이미 존재합니다.`);
      return;
    }

    if (editingId) {
      const before = effectiveMappings.find((mapping) => mapping.id === editingId);
      if (!before) {
        setStatusMessage("수정할 매핑을 찾지 못했습니다.");
        resetForm();
        return;
      }
      if (!runtime && activeMappingIds.has(editingId)) {
        setStatusMessage(
          `${before.sourceCardName} 매핑은 IMPORT_STARTED Group에서 사용 중이므로 수정할 수 없습니다.`
        );
        return;
      }
      const after: ErpCardMapping = {
        ...before,
        sourceCardName: source,
        targetErpCardName: target,
        version: before.version + 1,
        updatedAt: snapshot.capturedAt,
        updatedBy: snapshot.actor,
      };
      if (runtime) {
        if (!runtime.onUpdateMapping) {
          setStatusMessage("서버 Mapping 수정 기능이 미연결입니다.");
          return;
        }
        setRuntimeBusyAction(editingId);
        try {
          await runtime.onUpdateMapping(editingId, {
            sourceCardName: source,
            targetErpCardName: target,
          });
          const message = `${source} → ${target} 매핑을 서버에서 수정했습니다.`;
          setStatusMessage(message);
          runtime.onNotify?.(message);
          resetForm();
        } catch (error) {
          setStatusMessage(
            getErrorMessage(error, "ERP 카드사 매핑을 수정하지 못했습니다.")
          );
        } finally {
          setRuntimeBusyAction("");
        }
        return;
      }
      setMappings((current) => current.map((mapping) => (mapping.id === editingId ? after : mapping)));
      recordAudit("ERP_CARD_MAPPING_UPDATED", before, after);
      setStatusMessage(`${source} → ${target} 매핑을 synthetic local state에서 수정했습니다.`);
      resetForm();
      return;
    }

    const currentSequence = sequence.current;
    const created: ErpCardMapping = {
      id: `MAP-SIM-${currentSequence}`,
      sourceCardName: source,
      targetErpCardName: target,
      enabled: true,
      sourceType: "EXPLICIT",
      version: 1,
      createdAt: snapshot.capturedAt,
      updatedAt: snapshot.capturedAt,
      createdBy: snapshot.actor,
      updatedBy: snapshot.actor,
    };
    if (runtime) {
      if (!runtime.onCreateMapping) {
        setStatusMessage("서버 Mapping 등록 기능이 미연결입니다.");
        return;
      }
      setRuntimeBusyAction("CREATE");
      try {
        await runtime.onCreateMapping({
          sourceCardName: source,
          targetErpCardName: target,
          enabled: true,
        });
        const message = `${source} → ${target} 매핑을 서버에 등록했습니다.`;
        setStatusMessage(message);
        runtime.onNotify?.(message);
        resetForm();
      } catch (error) {
        setStatusMessage(
          getErrorMessage(error, "ERP 카드사 매핑을 등록하지 못했습니다.")
        );
      } finally {
        setRuntimeBusyAction("");
      }
      return;
    }
    setMappings((current) => [...current, created]);
    recordAudit("ERP_CARD_MAPPING_CREATED", null, created);
    setStatusMessage(`${source} → ${target} 매핑을 synthetic local state에 등록했습니다.`);
    resetForm();
  };

  const beginEdit = (mapping: ErpCardMapping) => {
    setEditingId(mapping.id);
    setSourceCardName(mapping.sourceCardName);
    setTargetErpCardName(mapping.targetErpCardName);
    setStatusMessage(`${mapping.sourceCardName} 매핑 수정값을 입력하세요.`);
  };

  const deleteMapping = async (mapping: ErpCardMapping) => {
    if (!runtime && activeMappingIds.has(mapping.id)) {
      setStatusMessage(
        `${mapping.sourceCardName} 매핑은 IMPORT_STARTED Group에서 사용 중이므로 삭제할 수 없습니다.`
      );
      return;
    }
    if (runtime) {
      if (!runtime.onDeleteMapping) {
        setStatusMessage("서버 Mapping 삭제 기능이 미연결입니다.");
        return;
      }
      setRuntimeBusyAction(mapping.id);
      try {
        await runtime.onDeleteMapping(mapping.id);
        const message = `${mapping.sourceCardName} 명시 매핑을 서버에서 삭제했습니다.`;
        setStatusMessage(message);
        runtime.onNotify?.(message);
        if (editingId === mapping.id) resetForm();
      } catch (error) {
        setStatusMessage(
          getErrorMessage(error, "ERP 카드사 매핑을 삭제하지 못했습니다.")
        );
      } finally {
        setRuntimeBusyAction("");
      }
      return;
    }
    setMappings((current) => current.filter((item) => item.id !== mapping.id));
    recordAudit("ERP_CARD_MAPPING_DELETED", mapping, null);
    if (editingId === mapping.id) resetForm();
    setStatusMessage(`${mapping.sourceCardName} 명시 매핑을 synthetic local state에서 삭제했습니다.`);
  };

  const toggleMapping = async (mapping: ErpCardMapping) => {
    if (!runtime && activeMappingIds.has(mapping.id)) {
      setStatusMessage(
        `${mapping.sourceCardName} 매핑은 IMPORT_STARTED Group에서 사용 중이므로 상태를 변경할 수 없습니다.`
      );
      return;
    }
    const after = {
      ...mapping,
      enabled: !mapping.enabled,
      version: mapping.version + 1,
      updatedAt: snapshot.capturedAt,
      updatedBy: snapshot.actor,
    };
    if (runtime) {
      if (!runtime.onToggleMapping) {
        setStatusMessage("서버 Mapping 상태 변경 기능이 미연결입니다.");
        return;
      }
      setRuntimeBusyAction(mapping.id);
      try {
        await runtime.onToggleMapping(mapping.id, after.enabled);
        const message = `${mapping.sourceCardName} 매핑을 서버에서 ${after.enabled ? "활성" : "비활성"}화했습니다.`;
        setStatusMessage(message);
        runtime.onNotify?.(message);
      } catch (error) {
        setStatusMessage(
          getErrorMessage(error, "ERP 카드사 매핑 상태를 변경하지 못했습니다.")
        );
      } finally {
        setRuntimeBusyAction("");
      }
      return;
    }
    setMappings((current) => current.map((item) => (item.id === mapping.id ? after : item)));
    recordAudit(
      after.enabled ? "ERP_CARD_MAPPING_ENABLED" : "ERP_CARD_MAPPING_DISABLED",
      mapping,
      after
    );
    setStatusMessage(
      `${mapping.sourceCardName} 매핑을 synthetic local state에서 ${after.enabled ? "활성" : "비활성"}화했습니다.`
    );
  };

  const refreshMasterPreview = async () => {
    if (runtime) {
      if (!runtime.onReload) {
        setStatusMessage("서버 ERP 운영 payload 새로고침 기능이 미연결입니다.");
        return;
      }
      setRuntimeBusyAction("RELOAD");
      try {
        await runtime.onReload();
        setStatusMessage("ERP 카드사 설정과 최신 Master Snapshot을 서버에서 다시 불러왔습니다.");
      } catch (error) {
        setStatusMessage(
          getErrorMessage(error, "ERP 카드사 설정을 다시 불러오지 못했습니다.")
        );
      } finally {
        setRuntimeBusyAction("");
      }
      return;
    }
    const currentSequence = sequence.current;
    sequence.current += 1;
    setMasterCapturedAt(`${snapshot.capturedAt} · UIA PREVIEW-${currentSequence}`);
    setStatusMessage("ERP 카드 목록 새로고침을 미리보기했습니다. UIA/API 호출은 0건입니다.");
  };

  const mutationBusy = Boolean(runtime?.saving || runtimeBusyAction);
  const createAvailable = !runtime || Boolean(runtime.onCreateMapping);
  const updateAvailable = !runtime || Boolean(runtime.onUpdateMapping);
  const deleteAvailable = !runtime || Boolean(runtime.onDeleteMapping);
  const toggleAvailable = !runtime || Boolean(runtime.onToggleMapping);
  const reloadAvailable = !runtime || Boolean(runtime.onReload);
  const effectiveMasterCapturedAt = runtime
    ? runtime.erpCardMaster?.capturedAt || "아직 수집되지 않음"
    : masterCapturedAt;
  const effectiveCapturedAt = runtime
    ? runtime.erpCardMaster?.capturedAt || runtime.parentBatches?.[0]?.createdAt || "서버 시각 미연결"
    : snapshot.capturedAt;

  return (
    <section className="space-y-4" aria-labelledby="erp-card-routing-title">
      <header className="border border-slate-200 bg-slate-950 p-5 text-white shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-wide text-cyan-300">
              {runtime ? "Card-routed import · Server-backed admin" : "Card-routed import · Field-confirmed QA"}
            </p>
            <h3 id="erp-card-routing-title" className="mt-1 text-lg font-black">
              ERP 카드 Routing · Parent Batch / Target Card Groups
            </h3>
            <p className="mt-2 max-w-3xl text-xs font-bold leading-5 text-slate-300">
              활성화된 최신 Production Mapping으로 source issuer를 target ERP card에 연결하고 카드별 Group/XLS를 생성합니다.
              실제 ERP Field 조회 검증 전에는 VERIFIED로 닫지 않습니다.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-[11px] font-black">
            <span className="bg-blue-100 px-3 py-2 text-blue-950">ERP_AUTO_IMPORT OFF</span>
            <span className="bg-red-100 px-3 py-2 text-red-950">ERP 전표 Write 0건</span>
            <span className="bg-emerald-100 px-3 py-2 text-emerald-950">Mapping CRUD ACTIVE</span>
            <span className="bg-slate-800 px-3 py-2 text-slate-100">{effectiveCapturedAt}</span>
          </div>
        </div>
        <p className="mt-4 bg-slate-900 px-3 py-2 text-xs font-bold text-cyan-100" role="status" aria-live="polite">
          {runtime?.error || (runtime?.loading ? "ERP 카드사 설정을 불러오는 중입니다." : statusMessage)}
        </p>
      </header>

      {runtime ? (
        <section className="border border-slate-200 bg-white p-5" aria-labelledby="erp-three-role-evidence-title">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-[11px] font-black uppercase tracking-wide text-blue-700">Server-backed evidence boundary</p>
              <h4 id="erp-three-role-evidence-title" className="mt-1 text-sm font-black text-slate-950">
                Header-only Schema → Preview PREPARE → PILOT_FINAL Import
              </h4>
              <p className="mt-1 text-xs font-bold text-slate-600">
                각 단계는 서로 승격되지 않습니다. 서버 evidence가 없거나 lock 값이 불완전하면 관리자 UI는 계속 차단합니다.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 text-[11px] font-black">
              <span className={`px-3 py-2 ${ownerRuleLockIntact ? "bg-emerald-100 text-emerald-900" : "bg-red-100 text-red-950"}`}>
                {ownerRuleLockIntact ? "PREVIEW IMPORT LOCKED" : "PREVIEW LOCK PENDING · ADMIN BLOCKED"}
              </span>
              <span className={`px-3 py-2 ${ownerRulePreview?.pilotLocked === true ? "bg-emerald-100 text-emerald-900" : "bg-red-100 text-red-950"}`}>
                {ownerRulePreview?.pilotLocked === true ? "PILOT_FINAL LOCKED" : "PILOT LOCK PENDING · ADMIN BLOCKED"}
              </span>
            </div>
          </div>

          <p className="mt-4 border border-amber-300 bg-amber-50 px-3 py-3 text-xs font-black text-amber-950" role="alert">
            PREVIEW 경고 · Preview XLS와 PREPARE evidence는 PILOT_FINAL 파일·승인서·Import receipt가 아닙니다. 실제 Import action은 이 화면에 없습니다.
          </p>

          <div className="mt-4 grid gap-4 xl:grid-cols-3">
            <article className="border border-slate-200 bg-slate-50 p-4" data-testid="header-only-schema-evidence">
              <div className="flex items-start justify-between gap-3">
                <div><p className="text-[11px] font-black text-slate-500">ROLE 1</p><h5 className="mt-1 text-sm font-black text-slate-950">Header-only Schema</h5></div>
                <span className={`px-2 py-1 text-[11px] font-black ${headerOnlySchemaReady ? "bg-emerald-100 text-emerald-900" : "bg-amber-100 text-amber-950"}`}>
                  {headerOnlySchemaReady ? "READY" : "PENDING"}
                </span>
              </div>
              <dl className="mt-3 space-y-2 text-xs">
                <div><dt className="font-bold text-slate-500">Workbook</dt><dd className="mt-1 font-black text-slate-900">{prepareOnlyEvidence?.xls?.fileName || "서버 evidence 미연결"}</dd></div>
                <div><dt className="font-bold text-slate-500">Sheet / Rows</dt><dd className="mt-1 font-black text-slate-900">{prepareOnlyEvidence?.xls?.sheetName || "PENDING"} · Header {prepareOnlyEvidence?.xls?.headerCount ?? "?"} · Data row {prepareOnlyEvidence?.xls?.dataRowCount ?? "?"}</dd></div>
                <div><dt className="font-bold text-slate-500">BIFF8 / SHA-256</dt><dd className="mt-1 break-all font-black text-slate-900">{prepareOnlyEvidence?.xls?.biff8Verified === true ? "VERIFIED" : "PENDING"} · {shortEvidenceHash(prepareOnlyEvidence?.xls?.sha256)}</dd></div>
              </dl>
            </article>

            <article className="border border-slate-200 bg-slate-50 p-4" data-testid="preview-prepare-evidence">
              <div className="flex items-start justify-between gap-3">
                <div><p className="text-[11px] font-black text-slate-500">ROLE 2</p><h5 className="mt-1 text-sm font-black text-slate-950">Preview PREPARE</h5></div>
                <span className={`px-2 py-1 text-[11px] font-black ${previewMetadataReady && prepareSideEffectLockIntact ? "bg-emerald-100 text-emerald-900" : "bg-amber-100 text-amber-950"}`}>
                  {previewMetadataReady && prepareSideEffectLockIntact ? "EVIDENCE CONNECTED" : "PENDING"}
                </span>
              </div>
              <dl className="mt-3 space-y-2 text-xs">
                <div><dt className="font-bold text-slate-500">Preview Snapshot</dt><dd className="mt-1 font-black text-slate-900">{ownerRulePreview?.snapshotId || "PENDING"} · {ownerRulePreview?.schemaVersion || "schema 미연결"}</dd></div>
                <div><dt className="font-bold text-slate-500">Group / Target</dt><dd className="mt-1 font-black text-slate-900">{ownerGroupPreview.length}개 · {ownerRulePreview?.selectedTarget?.targetErpCardName || "PENDING"}</dd></div>
                <div><dt className="font-bold text-slate-500">Snapshot Hash</dt><dd className="mt-1 font-black text-slate-900">{shortEvidenceHash(ownerRulePreview?.snapshotHash)}</dd></div>
                <div><dt className="font-bold text-slate-500">PREPARE result</dt><dd className={`mt-1 font-black ${prepareOnlyEvidence?.status === "ERP_FIELD_CONTRACT_PREPARED" ? "text-emerald-800" : "text-orange-900"}`}>{prepareOnlyEvidence?.status || "PENDING"}{prepareOnlyEvidence?.code ? ` · ${prepareOnlyEvidence.code}` : ""}</dd></div>
                <div><dt className="font-bold text-slate-500">Side effects</dt><dd className="mt-1 font-black text-slate-900">Import {prepareOnlyEvidence?.businessSideEffects?.importInvoke ?? "?"} · ERP Write {prepareOnlyEvidence?.businessSideEffects?.erpWrite ?? "?"} · Network {prepareOnlyEvidence?.businessSideEffects?.network ?? "?"}</dd></div>
              </dl>
            </article>

            <article className="border border-red-200 bg-red-50 p-4" data-testid="pilot-final-lock">
              <div className="flex items-start justify-between gap-3">
                <div><p className="text-[11px] font-black text-red-700">ROLE 3</p><h5 className="mt-1 text-sm font-black text-red-950">PILOT_FINAL Import</h5></div>
                <span className="bg-red-700 px-2 py-1 text-[11px] font-black text-white">LOCKED</span>
              </div>
              <p className="mt-3 text-xs font-bold leading-5 text-red-900">
                최신 Source 재조회, 새 Batch/Group, PILOT_FINAL XLS, signed receipt와 별도 Owner 승인이 모두 확인되기 전 Import 0을 유지합니다.
              </p>
              <p className="mt-3 bg-white px-3 py-2 text-xs font-black text-red-950">Admin 실행 callback 없음 · 자동 승격 없음 · 실제 Import action 없음</p>
            </article>
          </div>
        </section>
      ) : null}

      {runtime ? (
        <section className="border border-slate-200 bg-white p-5" aria-labelledby="owner-rule-preview-title">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h4 id="owner-rule-preview-title" className="text-sm font-black text-slate-950">
                Owner Rule Resolution Preview
              </h4>
              <p className="mt-1 text-xs font-bold text-slate-600">
                서버가 반환한 Entity·Card·Group 운영 증거만 표시합니다. 미연결 payload는 추정하지 않습니다.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 text-[11px] font-black">
              <span className={`px-3 py-2 ${ownerRuleLockIntact ? "bg-red-100 text-red-950" : "bg-amber-100 text-amber-950"}`}>
                {ownerRuleLockIntact ? "IMPORT LOCKED" : "IMPORT LOCK EVIDENCE PENDING"}
              </span>
              <span className={`px-3 py-2 ${ownerRulePreview?.pilotLocked === true ? "bg-red-100 text-red-950" : "bg-amber-100 text-amber-950"}`}>
                {ownerRulePreview?.pilotLocked === true ? "PILOT LOCKED" : "PILOT LOCK EVIDENCE PENDING"}
              </span>
              <span className={`px-3 py-2 ${ownerRulePreview ? "bg-blue-100 text-blue-950" : "bg-slate-200 text-slate-700"}`}>
                {ownerRulePreview?.ownerRuleVersion || "OWNER RULE PAYLOAD PENDING"}
              </span>
            </div>
          </div>

          {!ownerRulePreview ? (
            <p className="mt-4 border border-slate-200 bg-slate-50 px-3 py-3 text-xs font-bold text-slate-600">
              Entity/Card/Group owner resolution payload 미연결 · PENDING
            </p>
          ) : (
            <div className="mt-4 space-y-4">
              <div className="grid gap-4 xl:grid-cols-2">
                <section className="border border-slate-200 p-4" aria-labelledby="owner-entity-preview-title">
                  <div className="flex items-center justify-between gap-3">
                    <h5 id="owner-entity-preview-title" className="text-xs font-black text-slate-950">Entity Resolution</h5>
                    <span className={`px-2 py-1 text-[11px] font-black ${ownerEntityPreview.length ? "bg-emerald-100 text-emerald-900" : "bg-slate-200 text-slate-700"}`}>
                      {ownerEntityPreview.length ? `${ownerEntityPreview.length}건` : "PENDING"}
                    </span>
                  </div>
                  <ul className="mt-3 space-y-2">
                    {ownerEntityPreview.length ? ownerEntityPreview.map((entity, index) => (
                      <li key={`${entity.entityId || entity.canonicalEntityId || "entity"}-${index}`} className="grid gap-2 bg-slate-50 px-3 py-3 text-xs sm:grid-cols-[1fr_auto_1fr] sm:items-center">
                        <strong className="font-black text-slate-950">{getOwnerEntitySourceLabel(entity)}</strong>
                        <span className="font-black text-blue-700">→</span>
                        <span className="font-black text-blue-950">{entity.erpEntityName || "UNRESOLVED"}</span>
                        <span className="font-bold text-slate-600 sm:col-span-3">
                          Resolution source: {entity.resolutionSource || "미연결"}{entity.eventCount === undefined ? "" : ` · ${entity.eventCount}건`}
                        </span>
                      </li>
                    )) : (
                      <li className="bg-slate-50 px-3 py-3 text-xs font-bold text-slate-600">Entity resolution evidence PENDING</li>
                    )}
                  </ul>
                </section>

                <section className="border border-slate-200 p-4" aria-labelledby="owner-card-preview-title">
                  <div className="flex items-center justify-between gap-3">
                    <h5 id="owner-card-preview-title" className="text-xs font-black text-slate-950">Card Resolution</h5>
                    <span className={`px-2 py-1 text-[11px] font-black ${ownerCardPreview.length ? "bg-emerald-100 text-emerald-900" : "bg-slate-200 text-slate-700"}`}>
                      {ownerCardPreview.length ? `${ownerCardPreview.length}건` : "PENDING"}
                    </span>
                  </div>
                  <div className="mt-3 overflow-x-auto">
                    <table className="min-w-[560px] w-full text-left text-xs">
                      <thead className="bg-slate-100 text-slate-700"><tr><th className="px-3 py-2">Source</th><th className="px-3 py-2">Target</th><th className="px-3 py-2">Resolution</th><th className="px-3 py-2">건수</th></tr></thead>
                      <tbody>
                        {ownerCardPreview.length ? ownerCardPreview.map((card) => (
                          <tr key={`${card.sourceCardName}-${card.targetErpCardName}-${card.resolutionType}`} className="border-b border-slate-200">
                            <td className="px-3 py-3 font-black text-slate-950">{card.sourceCardName}</td>
                            <td className="px-3 py-3 font-black text-blue-950">{card.targetErpCardName || "UNRESOLVED"}</td>
                            <td className="px-3 py-3 font-black text-slate-700">{card.resolutionType}</td>
                            <td className="px-3 py-3 font-bold text-slate-600">{card.eventCount === undefined ? "-" : `${card.eventCount}건`}</td>
                          </tr>
                        )) : (
                          <tr><td className="px-3 py-3 font-bold text-slate-600" colSpan={4}>Card resolution evidence PENDING</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>
              </div>

              <section className="border border-slate-200 p-4" aria-labelledby="owner-group-preview-title">
                <div className="flex items-center justify-between gap-3">
                  <h5 id="owner-group-preview-title" className="text-xs font-black text-slate-950">Target Card Group Summary</h5>
                  <span className={`px-2 py-1 text-[11px] font-black ${ownerRuleLockIntact ? "bg-emerald-100 text-emerald-900" : "bg-amber-100 text-amber-950"}`}>
                    {ownerRuleLockIntact ? "PREVIEW ONLY · LOCKED" : "LOCK EVIDENCE PENDING"}
                  </span>
                </div>
                <div className="mt-3 overflow-x-auto">
                  <table className="min-w-[1100px] w-full text-left text-xs" data-testid="owner-group-summary-table">
                    <thead className="bg-slate-100 text-slate-700"><tr><th className="px-3 py-2">Group / Target</th><th className="px-3 py-2">Resolution</th><th className="px-3 py-2">Source breakdown</th><th className="px-3 py-2">승인</th><th className="px-3 py-2">취소</th><th className="px-3 py-2">건수</th><th className="px-3 py-2">양수</th><th className="px-3 py-2">음수</th><th className="px-3 py-2">Net</th></tr></thead>
                    <tbody>
                      {ownerGroupPreview.length ? ownerGroupPreview.map((group) => (
                        <tr key={group.groupId} className="border-b border-slate-200 align-top">
                          <td className="px-3 py-3"><strong className="block font-black text-slate-950">{group.groupId}</strong><span className="mt-1 block font-black text-blue-950">{group.targetErpCardName}</span></td>
                          <td className="px-3 py-3 font-black text-slate-700">{group.resolutionTypes.join(" + ") || "PENDING"}</td>
                          <td className="px-3 py-3"><ul className="space-y-1">{group.sourceBreakdown.map((source) => <li key={source.sourceCardName} className="font-bold text-slate-700"><strong>{source.sourceCardName}</strong> · {source.eventCount}건 · 승인 {source.approvalCount} / 취소 {source.cancellationCount} · Net {formatMoney(source.netAmount)}</li>)}</ul></td>
                          <td className="px-3 py-3 font-bold">{group.approvalCount}건</td>
                          <td className="px-3 py-3 font-bold">{group.cancellationCount}건</td>
                          <td className="px-3 py-3 font-black">{group.eventCount}건</td>
                          <td className="px-3 py-3 font-bold text-emerald-800">{formatMoney(group.positiveAmount)}</td>
                          <td className="px-3 py-3 font-bold text-red-800">{formatMoney(group.negativeAmount)}</td>
                          <td className="px-3 py-3 font-black text-slate-950">{formatMoney(group.netAmount)}</td>
                        </tr>
                      )) : (
                        <tr><td className="px-3 py-3 font-bold text-slate-600" colSpan={9}>Group summary evidence PENDING</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              {ownerRuleHasUnresolved ? (
                <section className="border border-red-300 bg-red-50 p-4" aria-labelledby="owner-unresolved-title" role="alert">
                  <h5 id="owner-unresolved-title" className="text-xs font-black text-red-950">OWNER RULE UNRESOLVED · Import/Pilot 금지</h5>
                  <ul className="mt-2 space-y-1 text-xs font-bold text-red-900">
                    {ownerRuleUnresolved.map((item, index) => <li key={`${item.kind}-${item.source}-${item.reasonCode}-${index}`}>{item.kind} · {item.source} · {item.reasonCode}</li>)}
                  </ul>
                </section>
              ) : ownerRuleEvidenceReady ? (
                <p className="border border-emerald-200 bg-emerald-50 px-3 py-3 text-xs font-black text-emerald-900">Owner Rule unresolved 0건</p>
              ) : (
                <p className="border border-slate-200 bg-slate-50 px-3 py-3 text-xs font-black text-slate-600">Owner Rule preview evidence PENDING · warning 없음</p>
              )}
            </div>
          )}
        </section>
      ) : null}

      <section className="border border-blue-200 bg-blue-50 p-5" aria-labelledby="card-routing-contract-title">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-[11px] font-black uppercase tracking-wide text-blue-700">Pilot operations contract</p>
            <h4 id="card-routing-contract-title" className="mt-1 text-lg font-black text-slate-950">
              Parent Batch → Target ERP Card Groups → Group XLS
            </h4>
            <p className="mt-2 text-xs font-bold leading-5 text-slate-700">
              Mapping은 active·productionAllowed·latest 증거가 있어야 Pilot에 사용할 수 있습니다. Mapping 미해결 Event는 CARD_MAPPING_REQUIRED로 분리합니다.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-[11px] font-black">
            <span className="bg-white px-3 py-2 text-slate-950">Production Mapping {productionMappings.length}개</span>
            <span className="bg-amber-100 px-3 py-2 text-amber-950">Persistence EXPECTED</span>
            <span className="bg-orange-100 px-3 py-2 text-orange-950">Field Verification {fieldVerificationStatus}</span>
          </div>
        </div>

        <p className="mt-4 border border-red-200 bg-white px-3 py-3 text-xs font-black text-red-900">
          Persistence EXPECTED · 가져오기 성공만으로 VERIFIED 금지 · 입금일자·거래처·금액·비고·승인번호 Field Verification PENDING
        </p>
      </section>

      <section
        className={`border p-5 ${doubleCheckMismatch ? "border-red-300 bg-red-50" : "border-slate-200 bg-white"}`}
        aria-labelledby="erp-vendor-field-contract-title"
      >
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h4 id="erp-vendor-field-contract-title" className="text-sm font-black text-slate-950">
              ERP Vendor Field Contract · Double Check
            </h4>
            <p className="mt-1 text-xs font-bold text-slate-600">
              운영 payload에서 확인된 값만 표시합니다. 미연결은 PENDING이며 MISMATCH는 Import와 VERIFIED를 차단합니다.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-[11px] font-black">
            <span className={`px-3 py-2 ${vendorFieldContractReady ? "bg-emerald-100 text-emerald-900" : "bg-amber-100 text-amber-950"}`}>
              Vendor Contract {vendorFieldContractReady ? "READY" : "PENDING"}
            </span>
            <span className={`px-3 py-2 ${doubleCheckMismatch ? "bg-red-700 text-white" : doubleCheckReady ? "bg-emerald-100 text-emerald-900" : "bg-amber-100 text-amber-950"}`}>
              Double Check {doubleCheckMismatch ? "MISMATCH · BLOCKED" : doubleCheckReady ? "MATCHED" : "PENDING"}
            </span>
          </div>
        </div>

        <dl className="mt-4 grid gap-3 lg:grid-cols-3">
          {[
            ["vendorFieldDesignation", operationalContract?.vendorFieldDesignation],
            ["vendorRecognitionMethod", operationalContract?.vendorRecognitionMethod],
            ["vendorFieldMapping", operationalContract?.vendorFieldMapping],
          ].map(([label, value]) => (
            <div key={label} className="border border-slate-200 bg-slate-50 px-3 py-3 text-xs">
              <dt className="font-black text-slate-600">{label}</dt>
              <dd className={`mt-2 break-words font-black ${value ? "text-slate-950" : "text-amber-800"}`}>
                {value || "미연결"}
              </dd>
            </div>
          ))}
        </dl>

        <ul className="mt-4 grid gap-3 lg:grid-cols-3" aria-label="ERP field contract Double Check">
          {doubleChecks.map((item) => (
            <li key={item.id} className="flex min-h-[54px] items-center justify-between gap-3 border border-slate-200 bg-white px-3 text-xs">
              <strong className="font-black text-slate-800">{item.label}</strong>
              <span className={`px-2 py-1 font-black ${item.status === "MATCHED" ? "bg-emerald-100 text-emerald-900" : item.status === "MISMATCH" ? "bg-red-100 text-red-900" : "bg-amber-100 text-amber-950"}`}>
                {item.status}
              </span>
            </li>
          ))}
        </ul>
        {doubleCheckMismatch ? (
          <p className="mt-4 border border-red-300 bg-white px-3 py-3 text-xs font-black text-red-900" role="alert">
            FIELD_CONTRACT_READBACK_MISMATCH · Import 0 · VERIFIED 금지
          </p>
        ) : null}
      </section>

      <div className="border border-slate-200 bg-white">
      <section className="border-t border-slate-200 p-5" aria-labelledby="mapping-crud-title">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h4 id="mapping-crud-title" className="text-sm font-black text-slate-950">ERP 카드사 매핑</h4>
            <p className="mt-1 text-xs font-bold text-slate-600">
              {runtime
                ? "IT_ADMIN API · 서버가 중복·권한·IMPORT_STARTED 보호의 최종 권위입니다."
                : "IT_ADMIN synthetic fixture · 변경은 브라우저 메모리에서만 유지됩니다."}
            </p>
          </div>
          <span className="bg-amber-100 px-3 py-2 text-[11px] font-black text-amber-950">
            IMPORT_STARTED 사용 매핑은 수정·삭제·상태변경 차단
          </span>
        </div>

        <form onSubmit={submitMapping} className="mt-4 grid gap-3 border border-blue-200 bg-blue-50 p-4 lg:grid-cols-[1fr_1fr_auto]">
          <label className="text-xs font-black text-slate-800">
            KCP 카드사
            <input
              value={sourceCardName}
              onChange={(event) => setSourceCardName(event.target.value)}
              disabled={runtime ? !(editingId ? updateAvailable : createAvailable) : false}
              className="mt-2 min-h-[42px] w-full border border-slate-300 bg-white px-3 text-sm font-bold text-slate-950"
              aria-label="KCP 카드사 입력"
            />
          </label>
          <label className="text-xs font-black text-slate-800">
            천년경영 카드사
            <input
              value={targetErpCardName}
              onChange={(event) => setTargetErpCardName(event.target.value)}
              disabled={runtime ? !(editingId ? updateAvailable : createAvailable) : false}
              className="mt-2 min-h-[42px] w-full border border-slate-300 bg-white px-3 text-sm font-bold text-slate-950"
              aria-label="천년경영 카드사 입력"
            />
          </label>
          <div className="flex items-end gap-2">
            <button type="submit" disabled={mutationBusy || (runtime ? !(editingId ? updateAvailable : createAvailable) : false)} className="min-h-[42px] border border-blue-700 bg-blue-700 px-4 text-xs font-black text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:border-slate-300 disabled:bg-slate-300">
              {mutationBusy ? "처리 중" : editingId ? "매핑 수정 저장" : "+ 매핑 추가"}
            </button>
            {editingId ? (
              <button type="button" onClick={resetForm} className="min-h-[42px] border border-slate-300 bg-white px-4 text-xs font-black text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
                취소
              </button>
            ) : null}
          </div>
        </form>

        <div className="mt-4 overflow-x-auto">
          <table
            className="min-w-[850px] w-full border-collapse text-left text-xs"
            data-testid="erp-card-mapping-table"
          >
            <thead className="bg-slate-100 text-slate-700">
              <tr>
                <th className="px-3 py-3 font-black">KCP 카드사</th>
                <th className="px-3 py-3 font-black">천년경영 카드사</th>
                <th className="px-3 py-3 font-black">상태</th>
                <th className="px-3 py-3 font-black">Production</th>
                <th className="px-3 py-3 font-black">Version</th>
                <th className="px-3 py-3 font-black">마지막 수정</th>
                <th className="px-3 py-3 font-black">작업</th>
              </tr>
            </thead>
            <tbody>
              {effectiveMappings.map((mapping) => {
                const active = activeMappingIds.has(mapping.id);
                return (
                  <tr key={mapping.id} className="border-b border-slate-200">
                    <td className="px-3 py-3 font-black text-slate-950">{mapping.sourceCardName}</td>
                    <td className="px-3 py-3 font-black text-blue-900">→ {mapping.targetErpCardName}</td>
                    <td className="px-3 py-3">
                      <span className={`px-2 py-1 font-black ${mapping.enabled ? "bg-emerald-100 text-emerald-900" : "bg-slate-200 text-slate-700"}`}>
                        {mapping.enabled ? "활성" : "비활성"}
                      </span>
                      {active ? <span className="ml-2 bg-amber-100 px-2 py-1 font-black text-amber-950">IMPORT_STARTED 사용 중</span> : null}
                    </td>
                    <td className="px-3 py-3 font-bold text-slate-700">
                      {mapping.productionAllowed === true ? "ALLOWED" : "BLOCKED"} · {mapping.latest === true ? "LATEST" : "STALE"} · {mapping.fieldConfirmation === "FIELD_CONFIRMED" ? "FIELD_CONFIRMED" : "UNCONFIRMED"}
                    </td>
                    <td className="px-3 py-3 font-bold text-slate-700">v{mapping.version}</td>
                    <td className="px-3 py-3 font-bold text-slate-600">{mapping.updatedAt}</td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap gap-2">
                        <button type="button" disabled={mutationBusy || !updateAvailable} onClick={() => beginEdit(mapping)} className="min-h-[36px] border border-blue-300 bg-blue-50 px-3 font-black text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50">수정</button>
                        <button type="button" disabled={mutationBusy || !toggleAvailable} onClick={() => void toggleMapping(mapping)} className="min-h-[36px] border border-slate-300 bg-white px-3 font-black text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50">{mapping.enabled ? "비활성" : "활성"}</button>
                        <button type="button" disabled={mutationBusy || !deleteAvailable} onClick={() => void deleteMapping(mapping)} className="min-h-[36px] border border-red-300 bg-red-50 px-3 font-black text-red-900 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50">삭제</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="mt-4 border border-amber-200 bg-amber-50 p-3 text-xs font-bold text-amber-950">
          자동 기본·동일명 Mapping은 없습니다. active·productionAllowed·latest·FIELD_CONFIRMED Explicit Mapping만 허용하며 ERP Master는 Target 존재 검증에만 사용합니다.
        </p>
      </section>

      <div className={runtime ? "grid gap-4" : "grid gap-4 xl:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]"}>
        <section className="min-w-0 border border-slate-200 bg-white p-5" aria-labelledby="erp-card-master-title">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h4 id="erp-card-master-title" className="text-sm font-black text-slate-950">천년경영 Card Master Snapshot</h4>
                {runtime ? (
                  <span className={`px-2 py-1 text-[11px] font-black ${cardMasterEvidenceReady ? "bg-emerald-100 text-emerald-900" : "bg-amber-100 text-amber-950"}`}>
                    {cardMasterEvidenceReady ? "SNAPSHOT READY" : "SNAPSHOT PENDING"}
                  </span>
                ) : null}
              </div>
              <p className="mt-1 text-xs font-bold text-slate-600">마지막 확인 {effectiveMasterCapturedAt}</p>
            </div>
            <button type="button" disabled={mutationBusy || !reloadAvailable} onClick={() => void refreshMasterPreview()} className="min-h-[42px] border border-blue-300 bg-blue-50 px-3 text-xs font-black text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50">
              {runtime ? "서버 설정 새로고침" : "ERP 카드 목록 새로고침"}
            </button>
          </div>
          <p className="mt-3 bg-amber-50 px-3 py-2 text-[11px] font-black text-amber-950">
            {runtime
              ? runtime.erpCardMaster
                ? `AGENT MASTER SNAPSHOT · ${runtime.erpCardMaster.agentId || "Agent 미확인"} · ${runtime.erpCardMaster.hostName || "Host 미확인"}`
                : "ERP CARD MASTER SNAPSHOT 없음 · 동일명칭 fallback 차단"
              : "SYNTHETIC_UIA_SNAPSHOT · 실제 ComboBox 조회 0건"}
          </p>
          {runtime ? (
            <dl className="mt-3 grid gap-2 text-[11px] sm:grid-cols-3">
              <div className="bg-slate-50 px-3 py-2"><dt className="font-bold text-slate-500">Visible options</dt><dd className="mt-1 font-black text-slate-900">{runtimeMasterCards.length}개</dd></div>
              <div className="bg-slate-50 px-3 py-2"><dt className="font-bold text-slate-500">Master hash</dt><dd className="mt-1 font-black text-slate-900">{shortEvidenceHash(runtime.erpCardMaster?.masterHash)}</dd></div>
              <div className="bg-slate-50 px-3 py-2"><dt className="font-bold text-slate-500">UI identity</dt><dd className="mt-1 font-black text-slate-900">{shortEvidenceHash(runtime.erpCardMaster?.uiIdentityHash)}</dd></div>
            </dl>
          ) : null}
          <ul className="mt-3 grid grid-cols-2 gap-2 text-xs font-black text-slate-800">
            {effectiveMasterCards.length ? effectiveMasterCards.map((card) => <li key={card} className="border border-slate-200 bg-slate-50 px-3 py-2">{card}</li>) : (
              <li className="col-span-2 border border-slate-200 bg-slate-50 px-3 py-2 text-amber-900">Card Master option evidence PENDING</li>
            )}
          </ul>
        </section>

        {runtime ? null : <section className="min-w-0 border border-slate-200 bg-white p-5" aria-labelledby="mapping-preview-title">
          <h4 id="mapping-preview-title" className="text-sm font-black text-slate-950">Effective Mapping Preview</h4>
          <p className="mt-1 text-xs font-bold text-slate-600">현재 미반영 Event의 source issuer → target ERP card입니다.</p>
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-[620px] w-full text-left text-xs">
              <thead className="bg-slate-100 text-slate-700"><tr><th className="px-3 py-2">KCP 카드사</th><th className="px-3 py-2">대기</th><th className="px-3 py-2">Effective ERP 카드</th><th className="px-3 py-2">판정</th></tr></thead>
              <tbody>
                {effectiveRoutes.map((route) => (
                  <tr key={route.sourceCardName} className="border-b border-slate-200">
                    <td className="px-3 py-3 font-black text-slate-950">{route.sourceCardName}</td>
                    <td className="px-3 py-3 font-bold text-slate-700">{route.eventCount}건 / {formatMoney(route.signedAmount)}</td>
                    <td className="px-3 py-3 font-black text-blue-900">{route.targetErpCardName || "매핑 필요"}</td>
                    <td className="px-3 py-3 font-bold text-slate-600">{route.resolution}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>}
      </div>

      {runtime ? null : <div className="grid gap-4 xl:grid-cols-[minmax(0,1.3fr)_minmax(0,0.7fr)]">
        <section className="border border-slate-200 bg-white p-5" aria-labelledby="effective-group-title">
          <h4 id="effective-group-title" className="text-sm font-black text-slate-950">Target ERP Card Group Preview</h4>
          <p className="mt-1 text-xs font-bold text-slate-600">Target 카드별 Group/XLS 생성 예상 결과입니다.</p>
          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            {effectiveGroups.map((group) => (
              <article key={group.targetErpCardName} className="border border-blue-200 bg-blue-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div><p className="text-[11px] font-black text-blue-700">ERP CARD</p><h5 className="mt-1 text-base font-black text-slate-950">{group.targetErpCardName}</h5></div>
                  <span className="bg-white px-3 py-2 text-xs font-black text-slate-900">{group.eventCount}건 / {formatMoney(group.signedAmount)}</span>
                </div>
                <ul className="mt-3 space-y-2">
                  {group.sources.map((source) => <li key={source.sourceCardName} className="flex justify-between gap-3 bg-white px-3 py-2 text-xs"><strong>{source.sourceCardName}</strong><span>{source.eventCount}건 / {formatMoney(source.signedAmount)}</span></li>)}
                </ul>
                <p className="mt-3 break-all bg-white px-3 py-2 text-[11px] font-black text-slate-700">
                  XLS {runtime
                    ? "서버 Planner 파일 미연결"
                    : snapshot.batch.groups.find((item) => item.targetErpCardName === group.targetErpCardName)?.fileName || "파일 생성 예정"}
                </p>
              </article>
            ))}
          </div>
        </section>

        <section className="border border-red-200 bg-red-50 p-5" aria-labelledby="mapping-required-title">
          <h4 id="mapping-required-title" className="text-sm font-black text-red-950">CARD_MAPPING_REQUIRED</h4>
          <p className="mt-1 text-xs font-bold text-red-800">미해결 Event는 Group/XLS/Pilot에서 제외됩니다.</p>
          <ul className="mt-4 space-y-2">
            {mappingRequired.length === 0 ? <li className="bg-white px-3 py-3 text-xs font-black text-emerald-800">매핑 필요 Event 없음</li> : mappingRequired.map((route) => (
              <li key={route.sourceCardName} className="bg-white px-3 py-3 text-xs"><strong className="block font-black text-red-950">{route.sourceCardName}</strong><span className="mt-1 block font-bold text-slate-700">{route.eventCount}건 / {formatMoney(route.signedAmount)} · CARD_MAPPING_REQUIRED</span></li>
            ))}
          </ul>
        </section>
      </div>}
      </div>

      {runtime ? (
        <section className="border border-slate-200 bg-white p-5" aria-labelledby="active-card-group-title">
          <h4 id="active-card-group-title" className="text-sm font-black text-slate-950">
            서버 Import Group History
          </h4>
          <p className="mt-1 text-xs font-bold text-slate-600">
            서버가 반환한 Group payload만 표시합니다. IMPORT_SUCCEEDED와 VERIFY_REQUIRED는 매핑 변경 보호 대상으로 표시합니다.
          </p>
          <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-black">
            <span className="bg-amber-100 px-2 py-1 text-amber-950">IMPORT_SUCCEEDED · 매핑 보호</span>
            <span className="bg-orange-100 px-2 py-1 text-orange-950">VERIFY_REQUIRED · 매핑 보호/자동 재전송 금지</span>
          </div>
          {runtimeGroupHistory.length === 0 ? (
            <p className="mt-4 bg-slate-50 px-3 py-3 text-xs font-bold text-slate-600">
              서버 Group history payload가 연결되지 않았습니다.
            </p>
          ) : (
            <div className="mt-4 grid gap-3 lg:grid-cols-3">
              {runtimeGroupHistory.map((group) => (
                <article key={group.groupId} className="border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-[11px] font-black text-slate-500">{group.parentBatchId || "Batch 미확인"}</p>
                      <h5 className="mt-1 text-sm font-black text-slate-950">{group.groupId}</h5>
                    </div>
                    <span className={`px-2 py-1 text-[11px] font-black ${isMappingProtectedGroupStatus(group.status) ? "bg-amber-100 text-amber-950" : "bg-slate-200 text-slate-800"}`}>
                      {group.status}
                    </span>
                  </div>
                  <p className="mt-3 text-xs font-black text-blue-900">ERP 카드 {group.targetErpCardName}</p>
                  <p className="mt-2 break-all bg-white px-2 py-2 text-[11px] font-black text-slate-800">
                    XLS {group.fileName || "파일 미연결"}
                  </p>
                  <p className="mt-2 text-[11px] font-bold text-slate-600">
                    Mapping snapshot {group.mappingSnapshot.length}개
                  </p>
                  <dl className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                    <div className="bg-white px-2 py-2"><dt className="font-bold text-slate-500">Event</dt><dd className="mt-1 font-black text-slate-900">{group.eventCount === undefined ? "미연결" : `${group.eventCount}건`}</dd></div>
                    <div className="bg-white px-2 py-2"><dt className="font-bold text-slate-500">Net</dt><dd className="mt-1 font-black text-slate-900">{group.netAmount === undefined ? "미연결" : formatMoney(group.netAmount)}</dd></div>
                    <div className="bg-white px-2 py-2"><dt className="font-bold text-slate-500">ERP 등록결과</dt><dd className={`mt-1 font-black ${group.importCountMatch ? "text-emerald-800" : "text-orange-900"}`}>{group.importRegisteredCount === null || group.importRegisteredCount === undefined ? "대기" : `${group.importRegisteredCount}/${group.importExpectedCount ?? group.eventCount ?? "?"}건`}</dd></div>
                    <div className="bg-white px-2 py-2"><dt className="font-bold text-slate-500">ERP 실제조회</dt><dd className={`mt-1 font-black ${group.lookupVerificationStatus === "VERIFIED" ? "text-emerald-800" : "text-orange-900"}`}>{group.lookupVerificationStatus === "VERIFIED" ? "완료" : group.lookupVerificationStatus || "대기"}</dd></div>
                  </dl>
                  {(group.importResultMessage || group.errorCode) ? (
                    <p className="mt-3 bg-white px-2 py-2 text-[11px] font-bold text-slate-700">
                      {group.importResultMessage || group.errorCode} · 자동 재전송 {group.automaticRetry === true ? "허용" : "금지"}
                    </p>
                  ) : null}
                  {group.lookupMatchChecks?.length ? (
                    <div className="mt-3 space-y-1 border-t border-slate-200 pt-3 text-[11px]">
                      {group.lookupMatchChecks.map((check) => (
                        <p key={check.eventId} className="break-all bg-white px-2 py-2 font-bold text-slate-700">
                          {check.eventId} · DATE {check.dateMatch ? "MATCH" : "STOP"} · VENDOR {check.vendorMatch ? "MATCH" : "STOP"} · AMOUNT {check.amountMatch ? "MATCH" : "STOP"} · NOTE {check.noteMatch ? "MATCH" : "STOP"}
                        </p>
                      ))}
                    </div>
                  ) : null}
                  {group.fourWayReconciliation ? (
                    <div className="mt-3 border-t border-slate-200 pt-3 text-[11px]">
                      <p className={`px-2 py-2 font-black ${group.fourWayReconciliation.matched ? "bg-emerald-100 text-emerald-900" : "bg-orange-100 text-orange-950"}`}>
                        FOUR-WAY {group.fourWayReconciliation.matched ? "MATCH" : "VERIFY_REQUIRED"}
                      </p>
                      <dl className="mt-2 space-y-1">
                        {([
                          ["SOURCE", group.fourWayReconciliation.source],
                          ["XLS", group.fourWayReconciliation.xls],
                          ["ERP RESULT", group.fourWayReconciliation.importResult],
                          ["ERP LOOKUP", group.fourWayReconciliation.lookup],
                        ] as const).map(([label, stage]) => (
                          <div key={label} className="bg-white px-2 py-2">
                            <dt className="font-black text-slate-500">{label}</dt>
                            <dd className="mt-1 font-bold text-slate-800">{formatFourWayStage(stage)}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  ) : null}
                  {group.verificationProbes?.length ? (
                    <div className="mt-3 border-t border-slate-200 pt-3 text-[11px]">
                      <p className="font-black text-slate-600">Double Check · {group.targetErpCardName} · {group.fileName || "XLS 미연결"} · Agent {group.agentId || "대기"}</p>
                      {group.verificationProbes.map((probe) => (
                        <p key={probe.eventId} className="mt-1 break-all bg-white px-2 py-2 font-bold text-slate-700">
                          {probe.occurredOn} · 거래처 {probe.vendorCode} · {formatMoney(probe.signedAmount)} · {probe.note}
                        </p>
                      ))}
                    </div>
                  ) : null}
                  {group.sourceCardBreakdown?.length ? (
                    <ul className="mt-3 space-y-1 border-t border-slate-200 pt-3 text-[11px]">
                      {group.sourceCardBreakdown.map((source) => (
                        <li key={source.sourceCardName} className="flex justify-between gap-2">
                          <strong>{source.sourceCardName}</strong>
                          <span>{source.eventCount}건 / {formatMoney(source.signedAmount)}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-3 border-t border-slate-200 pt-3 text-[11px] font-bold text-slate-500">
                      Source breakdown 미연결
                    </p>
                  )}
                </article>
              ))}
            </div>
          )}

          <div className="mt-5 border-t border-slate-200 pt-5">
            <h5 className="text-xs font-black text-slate-950">Parent Batch History</h5>
            {!runtime.parentBatches?.length ? (
              <p className="mt-3 bg-slate-50 px-3 py-3 text-xs font-bold text-slate-600">
                Parent Batch history payload가 연결되지 않았습니다.
              </p>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-[680px] w-full text-left text-xs">
                  <thead className="bg-slate-100 text-slate-700"><tr><th className="px-3 py-2">Batch</th><th className="px-3 py-2">상태</th><th className="px-3 py-2">Event</th><th className="px-3 py-2">Net</th><th className="px-3 py-2">VERIFIED</th><th className="px-3 py-2">생성</th></tr></thead>
                  <tbody>
                    {runtime.parentBatches.map((batch) => (
                      <tr key={batch.parentBatchId} className="border-b border-slate-200">
                        <td className="px-3 py-3 font-black text-slate-950">{batch.parentBatchId}</td>
                        <td className="px-3 py-3 font-black text-slate-700">{batch.status || "미확인"}</td>
                        <td className="px-3 py-3 font-bold">{batch.eventCount}건</td>
                        <td className="px-3 py-3 font-bold">{formatMoney(batch.netAmount)}</td>
                        <td className="px-3 py-3 font-bold">{batch.verifiedCount}건</td>
                        <td className="px-3 py-3 font-bold text-slate-600">{batch.createdAt || "미확인"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      ) : (
        <section className="border border-slate-200 bg-white p-5" aria-labelledby="batch-card-group-title">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div><p className="text-[11px] font-black text-blue-700">FIELD-CONFIRMED PARENT BATCH · QA</p><h4 id="batch-card-group-title" className="mt-1 text-lg font-black text-slate-950">{snapshot.batch.parentBatchId}</h4><p className="mt-1 text-xs font-bold text-slate-600">총 {snapshot.batch.eventCount}건 · Net {formatMoney(snapshot.batch.netAmount)}</p></div>
            <span className="bg-orange-100 px-3 py-2 text-xs font-black text-orange-950">{snapshot.batch.status}</span>
          </div>
          <div className="mt-4 grid gap-3 lg:grid-cols-3">
            {snapshot.batch.groups.map((group) => (
              <article key={group.groupId} className="border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-start justify-between gap-2"><div><p className="text-[11px] font-black text-slate-500">{group.groupId}</p><h5 className="mt-1 text-sm font-black text-slate-950">{group.targetErpCardName}</h5></div><span className={`px-2 py-1 text-[11px] font-black ${STATUS_STYLES[group.status]}`}>{STATUS_LABELS[group.status]}</span></div>
                <p className="mt-3 text-xs font-black text-slate-800">{group.eventCount}건 / {formatMoney(group.signedAmount)}</p>
                <p className="mt-2 break-all bg-white px-2 py-2 text-[11px] font-black text-slate-700">XLS {group.fileName || "미생성"}</p>
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="border border-slate-200 bg-white p-5" aria-labelledby="auto-import-gate-title">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)]">
          <div>
            <h4 id="auto-import-gate-title" className="text-sm font-black text-slate-950">ERP 자동 Import Gate</h4>
            <label className="mt-4 flex min-h-[54px] items-center justify-between gap-3 border border-red-200 bg-red-50 px-4 text-xs font-black text-red-950">
              <span>ERP 자동 Import 요청<span className="mt-1 block text-[11px] font-bold text-red-700">기본 OFF · 선택해도 모든 Pilot Gate 전에는 실행 불가</span></span>
              <input type="checkbox" role="switch" checked={autoImportRequested} disabled={Boolean(runtime)} onChange={(event) => { setAutoImportRequested(event.target.checked); setStatusMessage(event.target.checked ? "ERP 자동 Import ON 요청을 synthetic local state에만 반영했습니다. Pilot Gate가 잠겨 실행되지 않습니다." : "ERP 자동 Import 요청을 OFF로 변경했습니다."); }} aria-label="ERP 자동 Import 요청" className="h-5 w-5 accent-red-700 disabled:opacity-40" />
            </label>
            <dl className="mt-3 grid grid-cols-2 gap-2 text-xs"><div className="bg-slate-100 px-3 py-2"><dt className="font-bold text-slate-500">요청값</dt><dd className="mt-1 font-black text-slate-950">{autoImportRequested ? "ON" : "OFF"}</dd></div><div className="bg-slate-100 px-3 py-2"><dt className="font-bold text-slate-500">Effective</dt><dd className="mt-1 font-black text-slate-950">{effectiveAutoImportEnabled ? "ON" : "OFF · GATED"}</dd></div></dl>
          </div>
          <div>
            <div className="flex items-center justify-between gap-3"><h5 className="text-xs font-black text-slate-950">LIVE Pilot 필수 Gate</h5><span className="bg-slate-100 px-3 py-2 text-[11px] font-black text-slate-700">{readyGateCount}/{effectivePilotGates.length} READY</span></div>
            <ul className="mt-3 grid gap-2 sm:grid-cols-2">
              {effectivePilotGates.map((gate) => <li key={gate.id} className={`flex min-h-[42px] items-center justify-between gap-3 border px-3 text-xs font-black ${gate.ready ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-amber-200 bg-amber-50 text-amber-950"}`}><span>{gate.label}</span><span>{gate.ready ? "READY" : "PENDING"}</span></li>)}
            </ul>
          </div>
        </div>
      </section>

      {runtime ? (
        <details className="border border-slate-200 bg-white">
          <summary className="cursor-pointer px-5 py-4 text-sm font-black text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-600">
            Mapping Audit 안내
          </summary>
          <section className="border-t border-slate-200 p-5" aria-labelledby="mapping-audit-runtime-title">
            <h4 id="mapping-audit-runtime-title" className="text-sm font-black text-slate-950">Mapping Audit</h4>
            <p className="mt-3 bg-slate-50 px-3 py-3 text-xs font-bold text-slate-600">
              서버 Audit가 최종 이력입니다. Pilot은 active·productionAllowed·latest Mapping 증거가 모두 확인될 때만 진행합니다.
            </p>
          </section>
        </details>
      ) : (
        <section className="border border-slate-200 bg-white p-5" aria-labelledby="mapping-audit-title">
          <h4 id="mapping-audit-title" className="text-sm font-black text-slate-950">Mapping Audit · Synthetic session</h4>
          {audits.length === 0 ? <p className="mt-3 bg-slate-50 px-3 py-3 text-xs font-bold text-slate-600">이번 화면 세션의 변경 이력이 없습니다.</p> : <ul className="mt-3 space-y-2">{audits.map((audit) => <li key={audit.id} className="border border-slate-200 bg-slate-50 px-3 py-3 text-xs"><strong className="font-black text-slate-950">{audit.action}</strong><span className="ml-2 font-bold text-slate-600">{audit.actor} · {audit.timestamp}</span><span className="mt-1 block font-bold text-slate-700">{audit.before ? `${audit.before.sourceCardName} → ${audit.before.targetErpCardName} v${audit.before.version}` : "없음"} / {audit.after ? `${audit.after.sourceCardName} → ${audit.after.targetErpCardName} v${audit.after.version}` : "삭제됨"}</span></li>)}</ul>}
        </section>
      )}
    </section>
  );
};

export default ErpCardRoutingPanel;
